import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import StorageIcon from "@mui/icons-material/Storage";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import DnsIcon from "@mui/icons-material/Dns";
import WebIcon from "@mui/icons-material/Web";
import DomainIcon from "@mui/icons-material/Domain";
import SettingsIcon from "@mui/icons-material/Settings";
import SectionBackground from "./SectionBackground";
import ServiceModal from "./ServiceModal";

const services = [
  {
    icon: <StorageIcon />,
    title: "Shared Hosting Solutions",
    description:
      "Traditional shared hosting solutions perfect for small to medium websites with cost-effective pricing.",
    detailedDescription:
      "Our shared hosting solutions provide an affordable and reliable platform for hosting websites, blogs, and small business applications. Share server resources with other users while maintaining your own isolated hosting environment with dedicated storage and bandwidth allocations.",
    features: [
      "WHM/cPanel control panel access",
      "One-click application installers",
      "Email accounts with spam protection",
      "MySQL/PostgreSQL databases",
      "SSL certificates included",
      "Daily automated backups",
    ],
    benefits: [
      "Most cost-effective hosting option",
      "Easy to manage with intuitive control panels",
      "Perfect for beginners and small projects",
      "No server maintenance required",
      "Quick setup and deployment",
    ],
    idealFor: [
      "Personal blogs",
      "Small business websites",
      "Portfolio sites",
      "Low to medium traffic websites",
    ],
  },
  {
    icon: <SettingsIcon />,
    title: "VPS Hosting Solutions",
    description:
      "Virtual Private Server solutions offering the perfect balance between shared and dedicated hosting.",
    detailedDescription:
      "Virtual Private Servers provide dedicated resources within a virtualized environment, giving you more control and power than shared hosting at a fraction of dedicated server costs. Each VPS operates independently with guaranteed CPU, RAM, and storage allocations.",
    features: [
      "Guaranteed RAM and CPU allocation",
      "Full virtual machine root access",
      "Choice of Linux distributions",
      "SSD storage for faster performance",
      "Scalable resources",
      "Weekly automated backups",
    ],
    benefits: [
      "More control than shared hosting",
      "More affordable than dedicated servers",
      "Isolated environment for better security",
      "Easy to scale as you grow",
      "Better performance and stability",
    ],
    idealFor: [
      "Growing websites",
      "Medium-sized businesses",
      "Development servers",
      "Custom application hosting",
      "Multiple website hosting",
    ],
  },
  {
    icon: <DnsIcon />,
    title: "Dedicated Hosting Solutions",
    description:
      "Full dedicated server resources for your website or application with maximum control and performance.",
    detailedDescription:
      "Get complete control over your hosting environment with a dedicated physical server exclusively for your use. Enjoy maximum performance, security, and customization options without sharing resources with other users. Perfect for high-traffic websites and resource-intensive applications.",
    features: [
      "Full root/administrator access",
      "Choice of operating system (Linux/Windows)",
      "Customizable hardware configurations",
      "Advanced DDoS protection",
      "Dedicated IP addresses",
      "24/7 server monitoring and support",
      "Optional managed services",
    ],
    benefits: [
      "Maximum performance and reliability",
      "Complete control and customization",
      "Enhanced security and isolation",
      "No resource sharing with other users",
      "Scalable hardware upgrades",
    ],
    idealFor: [
      "High-traffic websites",
      "Enterprise applications",
      "E-commerce platforms",
      "Gaming servers",
      "Large databases",
    ],
  },
  {
    icon: <CloudQueueIcon />,
    title: "Cloud Hosting Solutions",
    description:
      "Scalable cloud infrastructure solutions designed to grow with your business needs.",
    detailedDescription:
      "Our cloud hosting solutions offer unparalleled flexibility and scalability, allowing you to easily adjust resources based on your current needs. Built on enterprise-grade infrastructure with automatic failover and load balancing for maximum uptime and performance.",
    features: [
      "Auto-scaling resources",
      "Pay-as-you-go pricing model",
      "Instant resource provisioning",
      "Load balancing and redundancy",
      "Global data center locations",
      "Real-time monitoring dashboard",
      "API access for automation",
    ],
    benefits: [
      "Scale resources up or down instantly",
      "Pay only for what you use",
      "99.99% uptime SLA",
      "Geographic redundancy",
      "Automatic failover protection",
    ],
    idealFor: [
      "Growing businesses",
      "Variable traffic websites",
      "SaaS applications",
      "Development and testing environments",
    ],
    comingSoon: true,
  },
  {
    icon: <WebIcon />,
    title: "Content Management Solution Hosting",
    description:
      "Specialized hosting for CMS platforms like WordPress, Drupal, and Joomla with optimized performance.",
    detailedDescription:
      "Our CMS-optimized hosting is specifically tuned for popular content management systems, providing enhanced performance, security, and ease of use. Pre-configured environments with automatic updates, caching, and security hardening for your chosen CMS platform.",
    features: [
      "Pre-installed CMS of your choice",
      "Automatic CMS updates",
      "CDN integration",
      "Enhanced security plugins",
      "Staging environment",
      "Daily automated backups",
      "One-click restore functionality",
    ],
    benefits: [
      "Optimized specifically for CMS performance",
      "Automatic security updates",
      "Expert CMS support available",
      "Pre-configured for best practices",
      "Faster page load times",
    ],
    idealFor: [
      "WordPress websites",
      "Drupal applications",
      "Joomla sites",
      "Content-heavy websites",
      "Marketing websites",
    ],
  },
  {
    icon: <DomainIcon />,
    title: "Domain Management",
    description:
      "Comprehensive domain management services including registration, transfers, and DNS configuration.",
    detailedDescription:
      "Simplify your domain management with our comprehensive services covering everything from registration to advanced DNS configuration. Manage all your domains from a single, intuitive control panel with advanced features like domain privacy protection and automatic renewal.",
    features: [
      "Domain registration and transfer",
      "Advanced DNS management",
      "Domain privacy protection (WHOIS)",
      "Email forwarding",
      "URL forwarding and redirection",
      "Subdomain management",
      "Auto-renewal options",
      "Bulk domain management",
    ],
    benefits: [
      "Centralized domain management",
      "Competitive pricing",
      "Free WHOIS privacy protection",
      "Easy DNS configuration",
      "Never lose a domain with auto-renewal",
    ],
    idealFor: [
      "Multiple domain portfolios",
      "Business domain management",
      "Domain investors",
      "Agency clients",
      "Brand protection",
    ],
  },
];

const Services: React.FC = () => {
  const [selectedService, setSelectedService] = useState<
    (typeof services)[0] | null
  >(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleLearnMore = (service: (typeof services)[0]) => {
    setSelectedService(service);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setTimeout(() => setSelectedService(null), 300);
  };

  return (
    <Box
      id="services"
      sx={{
        py: { xs: 8, md: 12 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <SectionBackground variant="default" />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              mb: 2,
              fontWeight: 700,
              fontSize: { xs: "2rem", md: "3rem" },
              background: "linear-gradient(135deg, #ffffff 0%, #00a8ff 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Our Hosted Solution Services
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: "text.secondary", maxWidth: 700, mx: "auto" }}
          >
            Comprehensive hosting solutions tailored to meet your unique
            requirements
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: "100%",
                  bgcolor: "background.paper",
                  border: "1px solid rgba(0, 168, 255, 0.2)",
                  transition: "all 0.3s ease",
                  position: "relative",
                  overflow: "visible",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 12px 40px rgba(0, 168, 255, 0.3)",
                    borderColor: "primary.main",
                    "& .service-icon": {
                      transform: "scale(1.1) rotate(5deg)",
                    },
                  },
                }}
              >
                <CardContent sx={{ p: 4, textAlign: "center" }}>
                  <Box
                    className="service-icon"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 80,
                      height: 80,
                      borderRadius: "16px",
                      background:
                        "linear-gradient(135deg, #00a8ff 0%, #0066cc 100%)",
                      mb: 3,
                      transition: "transform 0.3s ease",
                    }}
                  >
                    {React.cloneElement(service.icon, {
                      sx: { fontSize: 40, color: "white" },
                    })}
                  </Box>

                  {service.comingSoon && (
                    <Typography
                      sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        bgcolor: "primary.main",
                        color: "white",
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      Coming Soon
                    </Typography>
                  )}

                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                    {service.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mb: 3, lineHeight: 1.7 }}
                  >
                    {service.description}
                  </Typography>
                  <Button
                    variant="text"
                    onClick={() => handleLearnMore(service)}
                    sx={{
                      color: "primary.main",
                      "&:hover": {
                        bgcolor: "rgba(0, 168, 255, 0.1)",
                      },
                    }}
                  >
                    Learn More →
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <ServiceModal
        open={modalOpen}
        onClose={handleCloseModal}
        service={selectedService}
      />
    </Box>
  );
};

export default Services;
